import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { Button } from '../../components/common/Button'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface MarketPerformance {
  country: string
  seller_count: number
  avg_performance: number
  overperformers: number
  underperformers: number
  total_revenue: number
  market_potential: number
  utilization_rate: number
}

export const MarketPerformancePage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-market-performance'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/market-performance`)
      return data
    },
  })

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading market performance data...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading data: {(error as Error).message}</p>
          </Card>
        </main>
      </div>
    )
  }

  const markets: MarketPerformance[] = data?.markets || []
  const summary = data?.summary || {}

  const getPerformanceStatus = (utilization: number) => {
    if (utilization >= 80) return { label: 'Excellent', color: '#50C878' }
    if (utilization >= 60) return { label: 'Good', color: '#4A90E2' }
    if (utilization >= 40) return { label: 'Average', color: '#F59E0B' }
    return { label: 'Needs Attention', color: '#EF4444' }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Market Performance Analysis
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Compare seller performance against market potential across different markets
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Markets</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: colors.primary }}>{markets.length}</p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Overperformers</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#50C878' }}>{summary.total_overperformers || 0}</p>
              <p style={{ fontSize: '12px', color: colors.textSecondary }}>sellers exceeding targets</p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Underperformers</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#EF4444' }}>{summary.total_underperformers || 0}</p>
              <p style={{ fontSize: '12px', color: colors.textSecondary }}>sellers below targets</p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Avg Utilization</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#7B68EE' }}>{(summary.avg_utilization || 0).toFixed(1)}%</p>
              <p style={{ fontSize: '12px', color: colors.textSecondary }}>market potential used</p>
            </div>
          </Card>
        </div>

        {/* Markets Table */}
        <Card>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: colors.textPrimary }}>Market Performance Breakdown</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Market</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Sellers</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Revenue</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Potential</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Utilization</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Over/Under</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {markets.map((market, idx) => {
                  const status = getPerformanceStatus(market.utilization_rate)
                  return (
                    <tr key={market.country} style={{
                      borderBottom: `1px solid ${colors.border}`,
                      backgroundColor: idx % 2 === 0 ? 'transparent' : colors.background,
                    }}>
                      <td style={{ padding: '14px 12px', fontWeight: 500, color: colors.textPrimary }}>{market.country}</td>
                      <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>{market.seller_count}</td>
                      <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>${market.total_revenue.toLocaleString()}</td>
                      <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textSecondary }}>${market.market_potential.toLocaleString()}</td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <div style={{
                            width: '80px',
                            height: '8px',
                            backgroundColor: colors.border,
                            borderRadius: '4px',
                            overflow: 'hidden',
                          }}>
                            <div style={{
                              width: `${Math.min(market.utilization_rate, 100)}%`,
                              height: '100%',
                              backgroundColor: status.color,
                              borderRadius: '4px',
                            }} />
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: colors.textPrimary }}>
                            {market.utilization_rate.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                          <span style={{ color: '#50C878', fontWeight: 500 }}>+{market.overperformers}</span>
                          <span style={{ color: '#EF4444', fontWeight: 500 }}>-{market.underperformers}</span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          backgroundColor: status.color + '20',
                          color: status.color,
                          fontSize: '12px',
                          fontWeight: 600,
                        }}>
                          {status.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/markets/${market.country}`)}
                        >
                          Details
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
